import { useEffect, useState } from 'react'
import cx from 'classnames'
import { Elements } from '@stripe/react-stripe-js'
import { Tooltip } from 'antd'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

import Styles from './PaymentForm.module.scss'
import { CardForm } from './components/CardForm/CardForm'
import { ReactComponent as InfoIcon } from 'assets/icons/info16.svg'
import { Workspace } from 'types'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Radio, RadioGroup } from 'components/RadioGroup'
import { PaymentPlan, PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { BillingFrequency } from '../../UpgradePlanModal'
import { pluralize } from 'utils'
import { WorkspaceClient, PlanDetails } from 'api/clients/WorkspaceClient'
import { stripePromise } from 'components/UpdatePaymentMethodModal/UpdatePaymentMethodModal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { PAYMENT_SUCCEEDED_EVENT, trackEvent } from 'utils/trackingUtils'
import { useAuth } from 'hooks/UseAuth'
import { useMemberProfile } from 'hooks/useMemberProfile'
import { pricePerMonth, totalPrice } from 'components/Settings/TieredBilling/utils'
import { BillingFrequencyRadioButtons } from '../BillingFrequencyRadioButtons/BillingFrequencyRadioButtons'
import { Spinner } from 'common/Spinner/Spinner'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { upgradeOrDowngrade } from '../SelectPlan/components/PlanDetail/PlanDetail'
import { Modal } from 'almanac-ui/Modal'
import { useQueryParams } from 'hooks/useQueryParams'
import { usePromotionCode } from 'screens/LoginOrSignup/components/Blaze/StartFreeTrial/hooks/usePromotionCode'
import { formatPrice } from 'components/Settings/TieredBilling/utils'

export enum CancellationReason {
  TooExpensive = 'too_expensive',
  MissingFeatures = 'missing_features',
  SwitchedService = 'switched_service',
  Unused = 'unused',
  CustomerService = 'customer_service',
  TooComplex = 'too_complex',
  LowQuality = 'low_quality',
  Other = 'other',
}

interface Props {
  onClose: () => void
  onSuccess?: (paying: boolean, plan_details?: PlanDetails) => void
  selectedPlan: PaymentPlan
  savedPlan: PaymentPlan
  billingFrequency: BillingFrequency
  setBillingFrequency: (frequency: BillingFrequency) => void
  workspace: Workspace
  send: (event: string) => void
  startDate: string
  isCanceled?: boolean
}

export const PaymentForm = ({
  billingFrequency,
  onClose,
  onSuccess,
  selectedPlan,
  setBillingFrequency,
  savedPlan,
  workspace,
  send,
  startDate,
  isCanceled,
}: Props) => {
  const [isProcessing, setIsProcessing] = useState(false)

  const { isMobile } = useResponsiveDesign()
  const auth = useAuth()
  const { profile } = useMemberProfile(workspace.id, auth?.id)
  const [cancellationReason, setCancellationReason] = useState<CancellationReason | undefined>(undefined)
  const [cancellationDescription, setCancellationDescription] = useState<string>('')
  const [cancellationReasonError, setCancellationReasonError] = useState(false)
  const [cancellationDescriptionError, setCancellationDescriptionError] = useState(false)
  const { promotion_code } = useQueryParams()
  const { isFetched: promotionCodeIsFetched, data: promotionCodeData } = usePromotionCode(promotion_code)

  useEffect(() => {
    if (!cancellationDescriptionError) return

    if (!!cancellationDescription.length || cancellationReason !== CancellationReason.Other) {
      setCancellationDescriptionError(false)
    }
  }, [cancellationDescription, cancellationReason, cancellationDescriptionError])

  useEffect(() => {
    if (!cancellationReasonError) return

    if (!!cancellationReason) setCancellationReasonError(false)
  }, [cancellationReason, cancellationReasonError])

  const onGoBack = () => {
    send('SELECT_PLAN')
  }

  const finalizeTransaction = (success: boolean, paying: boolean, message?: string, planDetails?: PlanDetails) => {
    if (!success) {
      showToastMessage(message, 'error')
      setIsProcessing(false)
      return
    }

    send('PAYMENT_SUCCEEDED')
    setIsProcessing(false)

    if (onSuccess) onSuccess(paying, planDetails)
  }

  const cancelSubscription = async () => {
    if (!cancellationReason) {
      setCancellationReasonError(true)
      return
    }

    if (cancellationReason === CancellationReason.Other && !cancellationDescription.length) {
      setCancellationDescriptionError(true)
      return
    }

    setIsProcessing(true)

    const {
      data: { success, message },
    } = await WorkspaceClient.cancelSubscription({
      workspaceId: workspace.id,
      cancellationReason,
      cancellationDescription,
    })

    if (success) {
      showToastMessage('Subscription canceled 😭')
    }

    finalizeTransaction(success, true, message)
  }

  const createSubscription = async (paymentMethodId: string) => {
    if (!selectedPlan || !billingFrequency) return

    const priceId = selectedPlan.price[billingFrequency].price_id
    const couponId = selectedPlan.price[billingFrequency].coupon_id || promotionCodeData?.promotion_code?.coupon.id

    setIsProcessing(true)

    const res = await WorkspaceClient.createSubscription(
      workspace.id,
      paymentMethodId,
      selectedPlan.name.toLowerCase(),
      workspace.members_count,
      billingFrequency,
      priceId,
      undefined,
      couponId,
    )

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      const { plan_details, success, message } = res.data
      const transactionId = plan_details.subscription_id
      const price = parseFloat(plan_details.price)
      if (success) {
        trackEvent(PAYMENT_SUCCEEDED_EVENT, profile, { transactionId, price })
        showToastMessage('Subscription created 🎉')
      }

      finalizeTransaction(success, true, message, res.data.plan_details)
      return
    }

    finalizeTransaction(false, false, 'We were unable to process your payment')
  }

  const updateSubscription = async () => {
    if (!selectedPlan || !billingFrequency) return

    const priceId = selectedPlan.price[billingFrequency].price_id
    const couponId = selectedPlan.price[billingFrequency].coupon_id || promotionCodeData?.promotion_code?.coupon.id

    setIsProcessing(true)

    const {
      data: { success, message, plan_details },
    } = await WorkspaceClient.updateSubscription(workspace.id, priceId, couponId)

    if (success) {
      showToastMessage('Subscription updated 🎉')
    }

    finalizeTransaction(success, true, message, plan_details)
  }

  const isFreePlan = (selectedPlan?.key || savedPlan.key) === PaymentPlanKey.Free
  const amount_off =
    (promotionCodeIsFetched &&
      promotionCodeData?.promotion_code?.coupon.amount_off &&
      promotionCodeData.promotion_code.coupon.amount_off / 100) ||
    null
  const percent_off = (promotionCodeIsFetched && promotionCodeData?.promotion_code?.coupon.percent_off) || null
  const discountName = (promotionCodeIsFetched && promotionCodeData?.promotion_code?.coupon.name) || null
  const isDiscount = promotionCodeIsFetched && discountName && (amount_off || percent_off)
  const price = totalPrice(selectedPlan, billingFrequency, workspace.members_count)

  return (
    <>
      <Modal.Header onClose={onGoBack} title={isFreePlan ? 'Cancel Subscription' : 'Payment'} />
      {isProcessing && (
        <div className={Styles.overlay}>
          <Spinner />
        </div>
      )}
      <Modal.Content>
        <div className={Styles.subscriptionInfoContainer} data-testid="payment-header">
          <div className={Styles.subscriptionStartDate}>
            <span className={cx(Styles.subscriptionDescription, { [Styles.cancel]: isFreePlan })}>
              {isFreePlan
                ? "If you're sure you want to cancel, we would love your feedback - tell us why you are canceling this plan"
                : `Subscription to ${selectedPlan?.name || savedPlan.name} starts:`}
            </span>
            {!isFreePlan && <span>{startDate}</span>}
          </div>
          {!isFreePlan && (
            <div className={Styles.billingFrequencyContainer}>
              <BillingFrequencyRadioButtons
                billingFrequency={billingFrequency}
                setBillingFrequency={setBillingFrequency}
              />
            </div>
          )}
        </div>
        {isFreePlan && (
          <div className={Styles.cancellationContent}>
            <RadioGroup
              className={Styles.radioContainer}
              aria-label="Why would you like to cancel?"
              onChange={(reason: string) => setCancellationReason(reason as CancellationReason)}
              value={cancellationReason}
            >
              <Radio value={CancellationReason.TooExpensive} aria-label="It's too expensive">
                It's too expensive
              </Radio>
              <Radio value={CancellationReason.MissingFeatures} aria-label="I need more features">
                I need more features
              </Radio>
              <Radio value={CancellationReason.SwitchedService} aria-label="I found an alternative">
                I found an alternative
              </Radio>
              <Radio value={CancellationReason.Unused} aria-label="I no longer need it">
                I no longer need it
              </Radio>
              <Radio value={CancellationReason.Other} aria-label="Other reason">
                Other reason
              </Radio>
              {cancellationReasonError && <div className={Styles.validationError}>Please select a reason</div>}
            </RadioGroup>

            <Input
              isTextarea
              value={cancellationDescription}
              name="cancellationDescription"
              onChange={(e) => setCancellationDescription(e.currentTarget.value)}
              className={Styles.textInput}
              placeholder={
                cancellationReason === CancellationReason.Other ? 'Tell us more' : 'Any additional feedback?'
              }
            />
            {cancellationDescriptionError && <div className={Styles.validationError}>Please provide some feedback</div>}
          </div>
        )}
        {!isFreePlan && (
          <>
            <div className={Styles.priceDetails}>
              <div className={Styles.itemsTotal}>
                <div className={Styles.subscriptionDetailAndItemsTotal}>
                  <div className={Styles.subscriptionDetail}>
                    <span>
                      <span className={Styles.pricePerMonth}>${pricePerMonth(selectedPlan, billingFrequency)}</span>
                      <span className={Styles.usd}>USD</span>
                      <span className={Styles.multiply}>×</span>
                    </span>
                    <span>
                      <span className={Styles.twelve}>{billingFrequency === 'year' ? 12 : 1}</span>
                      <span className={Styles.months}>{pluralize('month', billingFrequency === 'year' ? 12 : 1)}</span>
                      {billingFrequency === 'year' && (
                        <span className={Styles.billedAnnuallyParens}>(billed annually)</span>
                      )}
                      <span className={Styles.info}>
                        <Tooltip
                          trigger="hover"
                          title="Edit billing frequency above or later in Settings"
                          placement="top"
                        >
                          <InfoIcon className={Styles.infoIcon} />
                        </Tooltip>
                      </span>
                    </span>
                  </div>
                  {!isMobile && (
                    <div className={Styles.itemTotal}>
                      <span>${formatPrice(price)} USD</span>
                    </div>
                  )}
                </div>
                {isDiscount && (
                  <div className={Styles.appliedDiscount}>
                    <div>
                      <span>{discountName}</span>
                    </div>
                    <div>
                      {amount_off && <div className={Styles.discount}>-${formatPrice(amount_off)}</div>}
                      {percent_off && (
                        <div className={Styles.discount}>-${formatPrice(price * (percent_off / 100))}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className={Styles.priceTotal}>
                <div>
                  <span>Total</span>
                </div>
                <div>
                  {amount_off && <div className={Styles.value}>${formatPrice(price - amount_off)} USD</div>}
                  {percent_off && <div className={Styles.value}>${formatPrice(price * (percent_off / 100))} USD</div>}
                  {!amount_off && !percent_off && <div className={Styles.value}>${formatPrice(price)} USD</div>}
                </div>
              </div>
            </div>
          </>
        )}
      </Modal.Content>
      {isFreePlan && (
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Modal.FooterButton isDisabled={isProcessing} onPress={onClose} size="lg" variant="secondary">
              <span>No, I'd like to keep my subscription</span>
            </Modal.FooterButton>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Modal.FooterButton onPress={cancelSubscription} size="lg" isDisabled={isProcessing} data-testid="upgrade">
              <span>{isProcessing ? 'Processing...' : 'Yes, cancel my subscription 😢'}</span>
            </Modal.FooterButton>
          </Modal.FooterContent>
        </Modal.Footer>
      )}
      {!isFreePlan && (
        <>
          {[PaymentPlanKey.Free, PaymentPlanKey.EarlyAdopter].includes(savedPlan.key) ? (
            <Elements stripe={stripePromise}>
              <CardForm
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
                onClose={onGoBack}
                onPaymentMethodCreated={createSubscription}
                buttonConfiguration={'multi-step'}
                dataHeapId={'payment-start'}
                allowCancel
              />
            </Elements>
          ) : (
            <Modal.Footer>
              <Modal.FooterContent slot="left">
                <Modal.FooterButton size="lg" isDisabled={isProcessing} onPress={onClose} variant="tertiary">
                  Cancel
                </Modal.FooterButton>
              </Modal.FooterContent>
              <Modal.FooterContent slot="right">
                <Modal.FooterButton
                  size="lg"
                  onPress={updateSubscription}
                  data-testid="upgrade"
                  isDisabled={isProcessing}
                >
                  {isProcessing ? (
                    <span>Processing...</span>
                  ) : (
                    <>
                      <span>{upgradeOrDowngrade(savedPlan, selectedPlan, isCanceled)}</span>
                      <span>&rarr;</span>
                    </>
                  )}
                </Modal.FooterButton>
              </Modal.FooterContent>
            </Modal.Footer>
          )}
        </>
      )}
    </>
  )
}
